import React, { useState } from "react";
import { Plus, Check, AlertCircle, ChevronRight, ExternalLink, MessageCircle, Home as HomeIcon, Compass } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { T, tr, ALLERGENS, INTOLERANCES, LABELS } from "../lib/i18n";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { Drawer, DrawerContent, DrawerClose } from "./ui/drawer";

const RECEPTION_WHATSAPP_NUMBER = "393518120603"; // +39 351 8120603
const ROOM_NAMES = { argo: "Argo", bestrla: "Bestla", clizio: "Clizio", damaseno: "Damaseno" };

function buildReceptionMessage(lang, roomName) {
  if (roomName) {
    const map = {
      it: `Buongiorno, sono un ospite della stanza "${roomName}". Ho bisogno di assistenza.`,
      en: `Hello, I'm a guest in room "${roomName}". I need assistance.`,
      es: `Hola, soy un huésped de la habitación "${roomName}". Necesito asistencia.`,
      fr: `Bonjour, je suis un client de la chambre "${roomName}". J'ai besoin d'assistance.`,
      de: `Guten Tag, ich bin Gast im Zimmer "${roomName}". Ich benötige Unterstützung.`,
    };
    return map[lang] || map.it;
  }
  const map = {
    it: "Buongiorno, sono un ospite della struttura. Ho bisogno di assistenza.",
    en: "Hello, I'm a guest at your property. I need assistance.",
    es: "Hola, soy un huésped del alojamiento. Necesito asistencia.",
    fr: "Bonjour, je suis un client de l'établissement. J'ai besoin d'assistance.",
    de: "Guten Tag, ich bin Gast in Ihrer Unterkunft. Ich benötige Unterstützung.",
  };
  return map[lang] || map.it;
}

export function DishCard({ dish, lang, onAdd, onView, isSelected }) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const openDetail = () => {
    if (hasLink) return;
    setDetailOpen(true);
    onView?.(dish);
  };
  const [variantOpen, setVariantOpen] = useState(false);
  const navigate = useNavigate();

  const hasImage = Boolean(dish.image);
  const desc = tr(dish.description, lang);
  const longDesc = desc && desc.length > 110;
  const displayDesc = expanded || !longDesc ? desc : desc.slice(0, 110) + "…";
  const hasLink = Boolean(dish.link);

  const handleAddClick = (e) => {
    e?.stopPropagation();
    if (dish.prices.length > 1) {
      setVariantOpen(true);
      setDetailOpen(false);
    } else {
      onAdd(dish, dish.prices[0]);
    }
  };

  const openLink = (e) => {
    e?.stopPropagation();
    if (!dish.link) return;
    if (dish.link.startsWith("http")) {
      window.open(dish.link, "_blank");
    } else {
      navigate(dish.link);
    }
  };

  return (
    <div className="group relative py-3.5 border-b border-neutral-200/70 last:border-b-0">
      <div className="flex gap-3 sm:gap-3.5 cursor-pointer" onClick={openDetail} data-testid={`dish-card-${dish.id}`}>
        <div className="flex-1 min-w-0 pr-1">
        {/* Name */}
        <h3 className="text-base sm:text-[17px] font-bold text-neutral-900 leading-snug tracking-tight">
          {tr(dish.name, lang)}
        </h3>

        {/* Description — 2 lines then truncated */}
        {desc && (
          <p className="mt-1 text-[13.5px] sm:text-sm text-neutral-500 leading-snug line-clamp-2">
            {desc}
          </p>
        )}

        {/* Prices — bullet list under description. The Allergens chip
            (when present) is appended inline to the last price so it doesn't
            steal a whole extra row. */}
        <div className="mt-1.5 space-y-0.5">
          {dish.prices.map((p, i) => {
            const isLast = i === dish.prices.length - 1;
            const hasAllergenInfo = dish.allergens.length > 0 || dish.mayContain.length > 0 || (dish.intolerances || []).length > 0;
            return (
              <div key={`${p.label ?? "std"}-${p.value}-${i}`} className="text-[13.5px] sm:text-sm leading-snug">
                {p.label && <span className="text-neutral-400">·&nbsp;</span>}
                {p.label && <span className="text-neutral-600">{p.label}: </span>}
                <span className="font-semibold text-neutral-900 whitespace-nowrap">
                  {p.value.toFixed(2)}&nbsp;€
                </span>
                {isLast && hasAllergenInfo && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        onClick={(e) => e.stopPropagation()}
                        className="ml-2 inline-flex items-center gap-0.5 text-[11.5px] text-neutral-500 hover:text-neutral-800 align-middle"
                        data-testid={`dish-allergens-${dish.id}`}
                      >
                        <AlertCircle className="h-3 w-3" />
                        <span className="underline underline-offset-2 decoration-dotted">{tr(T.allergens, lang)}</span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72" side="top">
                      <div className="text-xs font-semibold uppercase text-neutral-500">{tr(T.allergens, lang)}</div>
                      {dish.allergens.length > 0 && (
                        <div className="mt-2">
                          <div className="text-xs font-medium text-neutral-700">{tr(T.contains, lang)}</div>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {dish.allergens.map((code) => (
                              <span key={code} className="text-xs px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-100">
                                {tr(ALLERGENS[code] || { it: code }, lang)}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {dish.mayContain.length > 0 && (
                        <div className="mt-3">
                          <div className="text-xs font-medium text-neutral-700">{tr(T.mayContain, lang)}</div>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {dish.mayContain.map((code) => (
                              <span key={code} className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-100">
                                {tr(ALLERGENS[code] || { it: code }, lang)}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {(dish.intolerances || []).length > 0 && (
                        <div className="mt-3">
                          <div className="text-xs font-medium text-neutral-700">{tr(T.intolerances, lang)}</div>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {dish.intolerances.map((code) => (
                              <span key={code} className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-800 border border-indigo-100">
                                {tr(INTOLERANCES[code] || { it: code }, lang)}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Image + add button */}
      <div className="shrink-0 w-24 sm:w-28">
        {hasImage ? (
          <div className="relative w-24 h-24 sm:w-28 sm:h-28 rounded-lg overflow-hidden bg-neutral-100">
            <img
              src={dish.image}
              alt={tr(dish.name, lang)}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            {hasLink ? (
              <LinkButton dish={dish} lang={lang} onClick={openLink} />
            ) : (
              <AddButton isSelected={isSelected} open={open} setOpen={setOpen} dish={dish} lang={lang} onAdd={onAdd} onClick={handleAddClick} />
            )}
          </div>
        ) : (
          <div className="relative w-24 h-24 sm:w-28 sm:h-28 rounded-lg bg-neutral-50 border border-dashed border-neutral-200">
            {hasLink ? (
              <LinkButton dish={dish} lang={lang} onClick={openLink} />
            ) : (
              <AddButton isSelected={isSelected} open={open} setOpen={setOpen} dish={dish} lang={lang} onAdd={onAdd} onClick={handleAddClick} />
            )}
          </div>
        )}
      </div>
      </div>

      {/* Full-width CTA row when the dish is linked to a room / external target */}
      {hasLink && (
        <div className="mt-4 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={openLink}
              data-testid={`dish-link-${dish.id}`}
              className="w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-3 py-2.5 text-sm font-semibold shadow-sm transition-colors"
            >
              <HomeIcon className="h-4 w-4" />
              {tr({
                it: "Comanda la Stanza",
                en: "Control the Room",
                es: "Controla la Habitación",
                fr: "Piloter la Chambre",
                de: "Zimmer Steuern",
              }, lang)}
            </button>
            <a
              href={(() => {
                const slug = dish.link && dish.link.startsWith("/room/") ? dish.link.replace("/room/", "") : null;
                const roomName = slug ? ROOM_NAMES[slug] || slug.charAt(0).toUpperCase() + slug.slice(1) : null;
                const msg = buildReceptionMessage(lang, roomName);
                return `https://wa.me/${RECEPTION_WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;
              })()}
              target="_blank"
              rel="noreferrer"
              data-testid={`dish-reception-${dish.id}`}
              className="w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2.5 text-sm font-semibold shadow-sm transition-colors"
            >
              <MessageCircle className="h-4 w-4" />
              {tr({
                it: "Reception WhatsApp",
                en: "Reception WhatsApp",
                es: "Recepción WhatsApp",
                fr: "Réception WhatsApp",
                de: "Rezeption WhatsApp",
              }, lang)}
            </a>
          </div>
          {dish.link && dish.link.startsWith("/room/") && (
            <button
              onClick={() => navigate("/dintorni")}
              data-testid={`dish-dintorni-${dish.id}`}
              className="w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-white hover:bg-[#faf3ee] border border-[#4a1c17] text-[#4a1c17] px-3 py-2.5 text-sm font-semibold transition-colors"
            >
              <Compass className="h-4 w-4" />
              {tr({
                it: "Dintorni & Punti di Interesse",
                en: "Nearby Points of Interest",
                es: "Puntos de Interés Cercanos",
                fr: "Points d'Intérêt à Proximité",
                de: "Sehenswürdigkeiten in der Nähe",
              }, lang)}
            </button>
          )}
        </div>
      )}

      {/* Dish detail bottom sheet */}
      <Drawer open={detailOpen} onOpenChange={setDetailOpen}>
        <DrawerContent className="max-h-[92vh]" data-testid={`dish-drawer-${dish.id}`}>
          <div className="overflow-y-auto pb-6">
            {hasImage && (
              <div className="w-full bg-neutral-100">
                <img src={dish.image} alt={tr(dish.name, lang)} className="w-full h-auto object-cover max-h-[45vh]" />
              </div>
            )}
            <div className="px-5 pt-4">
              {dish.labels && dish.labels.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {dish.labels.map((code) => <LabelBadge key={code} code={code} lang={lang} />)}
                </div>
              )}
              <h2 className="text-2xl sm:text-3xl font-bold text-neutral-900 leading-tight tracking-tight">
                {tr(dish.name, lang)}
              </h2>
              <div className="mt-3 space-y-1">
                {dish.prices.map((p, i) => (
                  <div key={i} className="flex items-baseline gap-2 text-neutral-800">
                    <span className="text-neutral-400">·</span>
                    {p.label && <span>{p.label}:</span>}
                    <span className="font-semibold">{p.value.toFixed(2)} €</span>
                  </div>
                ))}
              </div>
              {desc && (
                <p className="mt-4 text-[15px] sm:text-base text-neutral-700 leading-relaxed whitespace-pre-line">
                  {desc}
                </p>
              )}
              {(dish.allergens.length > 0 || dish.mayContain.length > 0 || (dish.intolerances || []).length > 0) && (
                <div className="mt-5 rounded-lg bg-neutral-50 border border-neutral-200 p-3">
                  <div className="text-xs font-semibold uppercase text-neutral-500 mb-1.5">{tr(T.allergens, lang)}</div>
                  {dish.allergens.length > 0 && (
                    <div className="text-xs">
                      <span className="text-neutral-500">{tr(T.contains, lang)}: </span>
                      {dish.allergens.map((c) => tr(ALLERGENS[c] || { it: c }, lang)).join(" · ")}
                    </div>
                  )}
                  {dish.mayContain.length > 0 && (
                    <div className="text-xs mt-1">
                      <span className="text-neutral-500">{tr(T.mayContain, lang)}: </span>
                      {dish.mayContain.map((c) => tr(ALLERGENS[c] || { it: c }, lang)).join(" · ")}
                    </div>
                  )}
                  {(dish.intolerances || []).length > 0 && (
                    <div className="text-xs mt-1">
                      <span className="text-neutral-500">{tr(T.intolerances, lang)}: </span>
                      {dish.intolerances.map((c) => tr(INTOLERANCES[c] || { it: c }, lang)).join(" · ")}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="border-t border-neutral-100 p-4 bg-white">
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (dish.prices.length > 1) {
                  setVariantOpen(true);
                } else {
                  onAdd(dish, dish.prices[0]);
                  setDetailOpen(false);
                }
              }}
              data-testid={`dish-drawer-cta-${dish.id}`}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-neutral-900 hover:bg-black text-white px-5 py-3.5 text-base font-semibold"
            >
              {isSelected && <Check className="h-4 w-4" />}
              {dish.prices.length > 1
                ? tr({ it: "Seleziona variante", en: "Select variant", es: "Seleccionar variante", fr: "Choisir variante", de: "Variante wählen" }, lang)
                : `${tr({ it: "Aggiungi", en: "Add", es: "Añadir", fr: "Ajouter", de: "Hinzufügen" }, lang)} ${dish.prices[0].value.toFixed(2)} €`}
            </button>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Variant selection bottom sheet */}
      <Drawer open={variantOpen} onOpenChange={setVariantOpen}>
        <DrawerContent className="max-h-[75vh]" data-testid={`dish-variant-drawer-${dish.id}`}>
          <div className="px-5 pt-3 pb-2">
            <h3 className="text-center font-semibold text-lg text-neutral-900">
              {tr({ it: "Seleziona variante", en: "Select variant", es: "Seleccionar variante", fr: "Choisir variante", de: "Variante wählen" }, lang)}
            </h3>
          </div>
          <div className="border-t border-neutral-100 divide-y divide-neutral-100 overflow-y-auto">
            {dish.prices.map((p, i) => (
              <button
                key={`v-${i}`}
                onClick={() => { onAdd(dish, p); setVariantOpen(false); setDetailOpen(false); }}
                data-testid={`dish-variant-${dish.id}-${i}`}
                className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-neutral-50 text-left"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-base font-medium text-neutral-900 truncate">{p.label || tr(dish.name, lang)}</div>
                  <div className="mt-0.5 text-sm text-neutral-700">{p.value.toFixed(2)} €</div>
                </div>
                <span className="h-9 w-9 rounded-full border border-neutral-300 flex items-center justify-center text-neutral-700 hover:border-neutral-900 hover:text-neutral-900 shrink-0">
                  <Plus className="h-4 w-4" />
                </span>
              </button>
            ))}
          </div>
          <div className="border-t border-neutral-100 p-4 bg-white">
            <DrawerClose asChild>
              <button
                data-testid={`dish-variant-done-${dish.id}`}
                className="w-full rounded-full bg-neutral-900 hover:bg-black text-white px-5 py-3.5 text-base font-semibold"
              >
                {tr({ it: "Fatto", en: "Done", es: "Hecho", fr: "OK", de: "Fertig" }, lang)}
              </button>
            </DrawerClose>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}

function LinkButton({ dish, lang, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`dish-link-fab-${dish.id}`}
      className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full shadow-md flex items-center justify-center bg-[#4a1c17] text-white hover:bg-[#2b1410] transition-all"
      title={tr({ it: "Comanda la Stanza", en: "Control the Room", es: "Controla la Habitación", fr: "Piloter la Chambre", de: "Zimmer Steuern" }, lang)}
    >
      <HomeIcon className="h-4 w-4" />
    </button>
  );
}

function AddButton({ isSelected, open, setOpen, dish, lang, onAdd, onClick }) {
  const btnClass = `absolute -bottom-2 -right-2 h-8 w-8 rounded-full shadow-md flex items-center justify-center transition-all ${
    isSelected
      ? "bg-emerald-600 text-white"
      : "bg-white text-[#4a1c17] hover:bg-[#4a1c17] hover:text-white"
  }`;
  return (
    <button aria-label="add" onClick={(e) => { e.stopPropagation(); onClick(e); }} className={btnClass} data-testid={`dish-add-${dish.id}`}>
      {isSelected ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
    </button>
  );
}

function LabelBadge({ code, lang }) {
  const l = LABELS[code];
  if (!l) return null;
  const colorMap = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    red: "bg-red-50 text-red-700 border-red-100",
    amber: "bg-amber-50 text-amber-800 border-amber-100",
    green: "bg-green-50 text-green-700 border-green-100",
    rose: "bg-rose-50 text-rose-700 border-rose-100",
    blue: "bg-blue-50 text-blue-700 border-blue-100",
    orange: "bg-orange-50 text-orange-700 border-orange-100",
    slate: "bg-slate-50 text-slate-700 border-slate-200"
  };
  return (
    <span className={`text-[11px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded border ${colorMap[l.color] || colorMap.slate}`}>
      {tr(l, lang)}
    </span>
  );
}
